import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Download, Minus, Plus } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DataTable from "react-data-table-component";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import GeneralFooter from "../common/GeneralFooter";
import Loader from "../partials/Loader";
import * as XLSX from "xlsx";
import { handleAuthError } from "../../utils/functions";

// API URL configuration with fallback
const API_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8000";

const MPIHousehold = () => {
  const loading = useSelector((state) => state);
  const token = loading?.auth?.token;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [households, setHouseholds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loader, setLoader] = useState(false);

  // Filter states
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [villages, setVillages] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedDeh, setSelectedDeh] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);

  const [tempDistrict, setTempDistrict] = useState(selectedDistrict);
  const [tempTehsil, setTempTehsil] = useState(selectedTehsil);
  const [tempDeh, setTempDeh] = useState(selectedDeh);
  const [tempVillage, setTempVillage] = useState(selectedVillage);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [tempStatus, setTempStatus] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  // Fetch districts on mount
  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/districts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDistricts(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsils = async () => {
    if (!tempDistrict) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/tehsils?district_id=${tempDistrict}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTehsils(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching tehsils:", error);
    }
  };

  const fetchDehs = async () => {
    if (!tempTehsil) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/dehs?tehsil_id=${tempTehsil}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDehs(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching dehs:", error);
    }
  };

  const fetchVillages = async () => {
    if (!tempDeh) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/villages?deh_id=${tempDeh}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setVillages(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching villages:", error);
    }
  };

  useEffect(() => {
    fetchTehsils();
  }, [tempDistrict]);

  useEffect(() => {
    fetchDehs();
  }, [tempTehsil]);

  useEffect(() => {
    fetchVillages();
  }, [tempDeh]);

  const applyFilters = () => {
    if (!tempDistrict) {
      toast.warning("Please select at least a district");
      return;
    }
    setSelectedDistrict(tempDistrict);
    setSelectedTehsil(tempTehsil);
    setSelectedDeh(tempDeh);
    setSelectedVillage(tempVillage);
    setSelectedStatus(tempStatus);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setSelectedDistrict(null);
    setSelectedTehsil(null);
    setSelectedDeh(null);
    setSelectedVillage(null);
    setSelectedStatus(null);
    setTempDistrict(null);
    setTempTehsil(null);
    setTempDeh(null);
    setTempVillage(null);
    setTempStatus(null);
    setTehsils([]);
    setDehs([]);
    setVillages([]);
    setHouseholds([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const latestRequest = useRef(0);

  const fetchMPIHouseholds = async (page = 1) => {
    if (!selectedDistrict) return;

    setIsLoading(true);
    setLoader(true);
    try {
      let url = `${API_URL}/api/mpi-households?page=${page}&limit=${pagination.pageSize}`;

      if (selectedDistrict) url += `&district_id=${selectedDistrict}`;
      if (selectedTehsil) url += `&tehsil_id=${selectedTehsil}`;
      if (selectedDeh) url += `&deh_id=${selectedDeh}`;
      if (selectedVillage) url += `&village_id=${selectedVillage}`;
      if (selectedStatus !== null && selectedStatus !== undefined)
        url += `&is_poor=${selectedStatus}`;

      const requestNumber = ++latestRequest.current;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (requestNumber !== latestRequest.current) return;

      if (response.data.success) {
        setHouseholds(response.data.data.data || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.data.pagination.total_pages,
          totalItems: response.data.data.pagination.total_records,
        }));
      }
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching MPI households:", error);
      toast.error("Failed to fetch MPI household data");
    } finally {
      setIsLoading(false);
      setLoader(false);
    }
  };

  useEffect(() => {
    if (selectedDistrict) {
      fetchMPIHouseholds(pagination.currentPage);
    }
  }, [
    selectedDistrict,
    selectedTehsil,
    selectedDeh,
    selectedVillage,
    selectedStatus,
    pagination.currentPage,
    pagination.pageSize,
  ]);

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const toggleFilters = () => setIsFiltersVisible((prev) => !prev);

  const exportToExcel = async () => {
    if (households.length === 0) {
      toast.warning("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      let url = `${API_URL}/api/mpi-households?limit=999999&page=1`;

      if (selectedDistrict) url += `&district_id=${selectedDistrict}`;
      if (selectedTehsil) url += `&tehsil_id=${selectedTehsil}`;
      if (selectedDeh) url += `&deh_id=${selectedDeh}`;
      if (selectedVillage) url += `&village_id=${selectedVillage}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const exportData = response.data.data.data || [];

      // Helper function to check deprivation status
      const getDeprivationStatus = (value) => {
        if (value === null || value === undefined) return "Not Deprived";
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        return !isNaN(numValue) && numValue > 0 ? "Deprived" : "Not Deprived";
      };

      // Helper function to safely format numbers
      const formatNumber = (value, decimals = 2) => {
        if (value === null || value === undefined) return "0.00";
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        return isNaN(numValue) ? "0.00" : numValue.toFixed(decimals);
      };

      const excelData = exportData.map((hh, index) => ({
        "Sr.No": index + 1,
        "Household Number": hh.household_number || "-",
        CNIC: hh.hh_head_cnic_no || "-",
        "Years of Schooling": getDeprivationStatus(hh.mpi_years_of_schooling),
        "Child School Attendance": getDeprivationStatus(
          hh.mpi_child_school_attendance,
        ),
        "School Quality": getDeprivationStatus(hh.mpi_school_quality),
        "Access to Health": getDeprivationStatus(
          hh.mpi_access_to_health_facility,
        ),
        Immunization: getDeprivationStatus(hh.mpi_immunization),
        "Ante-natal Care": getDeprivationStatus(hh.mpi_ante_natal_care),
        "Assisted Delivery": getDeprivationStatus(hh.mpi_assisted_delivery),
        "Drinking Water": getDeprivationStatus(hh.mpi_drinking_water),
        Sanitation: getDeprivationStatus(hh.mpi_sanitation_facility),
        "Wall Material": getDeprivationStatus(hh.mpi_wall_material),
        Overcrowding: getDeprivationStatus(hh.mpi_overcrowding),
        Electricity: getDeprivationStatus(hh.mpi_electricity_access),
        "Cooking Fuel": getDeprivationStatus(hh.mpi_cooking_fuel),
        Assets: getDeprivationStatus(hh.mpi_assets_ownership),
        "Land/Livestock": getDeprivationStatus(hh.mpi_land_livestock),
        "Total Score": formatNumber(hh.mpi_total_score, 2),
        Status: hh.mpi_is_poor ? "Poor" : "Not Poor",
        "Weighted Score": formatNumber(hh.mpi_weighted_score, 2),
        "Censored Score": formatNumber(hh.mpi_censored_score, 2),
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 20 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "MPI Household");
      const currentDate = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `MPI_Household_Export_${currentDate}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setIsExporting(false);
    }
  };

  const renderDeprivationCell = (value) => {
    // If value is null or undefined, it means NOT DEPRIVED
    if (value === null || value === undefined) {
      return <span className="text-green-600 font-semibold">Not Deprived</span>;
    }

    // Convert to number if it's a string
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    // If it's a valid number (weight percentage), it means DEPRIVED
    if (!isNaN(numValue) && numValue > 0) {
      return <span className="text-red-600 font-semibold">Deprived</span>;
    }

    // For any other case (like 0 or invalid), treat as not deprived
    return <span className="text-green-600 font-semibold">Not Deprived</span>;
  };

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem]">HH Number</span>,
      selector: (row) => row.household_number || "-",
      width: "150px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">CNIC</span>,
      selector: (row) => row.hh_head_cnic_no || "-",
      width: "160px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Years of Schooling</span>,
      cell: (row) => renderDeprivationCell(row.mpi_years_of_schooling),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Child School</span>,
      cell: (row) => renderDeprivationCell(row.mpi_child_school_attendance),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">School Quality</span>,
      cell: (row) => renderDeprivationCell(row.mpi_school_quality),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Health Access</span>,
      cell: (row) => renderDeprivationCell(row.mpi_access_to_health_facility),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Immunization</span>,
      cell: (row) => renderDeprivationCell(row.mpi_immunization),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Ante-natal</span>,
      cell: (row) => renderDeprivationCell(row.mpi_ante_natal_care),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Delivery</span>,
      cell: (row) => renderDeprivationCell(row.mpi_assisted_delivery),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Water</span>,
      cell: (row) => renderDeprivationCell(row.mpi_drinking_water),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Sanitation</span>,
      cell: (row) => renderDeprivationCell(row.mpi_sanitation_facility),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Wall</span>,
      cell: (row) => renderDeprivationCell(row.mpi_wall_material),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Crowding</span>,
      cell: (row) => renderDeprivationCell(row.mpi_overcrowding),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Electricity</span>,
      cell: (row) => renderDeprivationCell(row.mpi_electricity_access),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Cooking Fuel</span>,
      cell: (row) => renderDeprivationCell(row.mpi_cooking_fuel),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Assets</span>,
      cell: (row) => renderDeprivationCell(row.mpi_assets_ownership),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Land/Livestock</span>,
      cell: (row) => renderDeprivationCell(row.mpi_land_livestock),
      width: "150px",
    },
    {
      name: <span className="text-[0.813rem]">Total Score</span>,
      selector: (row) => {
        const score = parseFloat(row.mpi_total_score);
        return isNaN(score) ? "0.00" : score.toFixed(2);
      },
      width: "120px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => (
        <span
          className={`py-1 px-2 text-[0.75em] rounded ${
            row.mpi_is_poor ? "bg-red-500 text-white" : "bg-success text-white"
          }`}
        >
          {row.mpi_is_poor ? "Poor" : "Not Poor"}
        </span>
      ),
      width: "120px",
    },
    {
      name: <span className="text-[0.813rem]">Weighted</span>,
      selector: (row) => {
        const score = parseFloat(row.mpi_weighted_score);
        return isNaN(score) ? "0.00" : score.toFixed(2);
      },
      width: "120px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Censored</span>,
      selector: (row) => {
        const score = parseFloat(row.mpi_censored_score);
        return isNaN(score) ? "0.00" : score.toFixed(2);
      },
      width: "120px",
      sortable: true,
    },
  ];

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            MPI Household
          </h1>
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              disabled={isExporting || households.length === 0}
              className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                isExporting || households.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <Download size={16} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>
        </div>

        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus size={14} color="#ffff" />
              )
            }
            onClick={toggleFilters}
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": { color: "#fff !important" },
            }}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="flex gap-5 w-full">
              <ThinDropdownWithSearch required={false}
                label="District"
                options={districts?.map((d) => ({
                  value: d.district_id,
                  label: d.district_name,
                }))}
                value={tempDistrict}
                onChange={(val) => {
                  setTempDistrict(val);
                  setTempTehsil(null);
                  setTempDeh(null);
                  setTempVillage(null);
                }}
              />
              <ThinDropdownWithSearch required={false}
                label="Tehsil"
                options={tehsils?.map((d) => ({
                  value: d.tehsil_id,
                  label: d.tehsil_name,
                }))}
                value={tempTehsil}
                onChange={(val) => {
                  setTempTehsil(val);
                  setTempDeh(null);
                  setTempVillage(null);
                }}
              />
              <ThinDropdownWithSearch required={false}
                label="Deh"
                options={dehs?.map((d) => ({
                  value: d.deh_id,
                  label: d.deh_name,
                }))}
                value={tempDeh}
                onChange={(val) => {
                  setTempDeh(val);
                  setTempVillage(null);
                }}
              />
              <ThinDropdownWithSearch required={false}
                label="Village"
                options={villages?.map((d) => ({
                  value: d.village_id,
                  label: d.village_name,
                }))}
                value={tempVillage}
                onChange={setTempVillage}
              />
              <ThinDropdownWithSearch required={false}
                label="Status"
                options={[
                  { value: true, label: "Poor" },
                  { value: false, label: "Not Poor" },
                ]}
                value={tempStatus}
                onChange={setTempStatus}
              />
              <div className="flex gap-2 items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#997800]"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white rounded-lg hover:bg-[#0f5f5f]"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              MPI Household List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
            </div>

            {households.length === 0 && !isLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {selectedDistrict
                    ? "No MPI data found for the selected filters"
                    : "Please select filters to view MPI household data"}
                </p>
              </div>
            ) : (
              <>
                <DataTable
                  columns={columns}
                  persistTableHead
                  data={households}
                  customStyles={customStyles}
                  highlightOnHover
                  progressPending={isLoading}
                />
                <GeneralFooter
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  handlePageSizeChange={handlePageSizeChange}
                  dataLength={households.length}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MPIHousehold;
